import OpenDiscussionButton from "../TicketList/OpenDiscussionButton/OpenDiscussionButton";
import { useEffect, useState } from "react";
import Button from "@mui/material/Button";
import { toast } from "react-toastify";
import { TextField, FormControl, Select, MenuItem } from "@mui/material";
import Popup from "reactjs-popup";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { useDispatch, useSelector } from "react-redux";
import { updateTicket, assignTicketToMe, fetchTicketById, setSelectedTicket, assignTicketToSupport } from '../../../../slices/TicketSlice';
import { fetchSupportUsers } from '../../../../slices/userSlice';  
import Draggable from "react-draggable";
import TicketChickenTest from "./TicketChickenTest/TicketChickenTest";
import Tooltip from "@mui/material/Tooltip";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import "./TicketDetailedView.scss";

const STATUS_COLORS = {
  UNASSIGNED: "#686868",
  IN_PROGRESS: "#159515",
  BLOCKED: "#DE3063",
  RESOLVED: "#185595",
  CLOSED: "#B3192B",
};

const STATUS_LABELS = {
  UNASSIGNED: "Unassigned",
  IN_PROGRESS: "In Progress",
  BLOCKED: "Blocked",
  RESOLVED: "Resolved",
  CLOSED: "Closed",
};

export default function TicketDetailedView({ onClose }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const ticket = useSelector((state) => state.tickets.selectedTicket);
  const user = useSelector((state) => state.user.user);
  const supportUsers = useSelector((state) => state.user.usersList);
  const [selectedId, setSelectedId] = useState(
    ticket?.assigneeId || "UNASSIGNED"
  );
  const [showEdit, setShowEdit] = useState(false);
  const [editFields, setEditFields] = useState({
    assigneeName: ticket?.assigneeName || "",
    description: ticket?.description || "",
  });

  const roles = user?.roles?.map((role) => role.authority) || [];
  const hasRole = (roleName) => roles.includes(roleName);
  const theme = createTheme({
    typography: { fontFamily: "'Poppins', sans-serif" },
  });

  useEffect(() => {
    if (ticket?.id && !ticket.description) {
      dispatch(fetchTicketById(ticket.id));
    }
  }, [dispatch, ticket?.id, ticket?.description]);

  useEffect(() => {
    if (hasRole("ROLE_ADMIN")) dispatch(fetchSupportUsers());
  }, [dispatch, user]);

  useEffect(() => {
    if (ticket) setSelectedId(ticket.assigneeId || "UNASSIGNED");
  }, [ticket?.assigneeId]);

  if (!ticket) return null;

  const normalizedStatus = String(ticket.status || "UNASSIGNED").toUpperCase();
  const statusClass = `status-${normalizedStatus
    .toLowerCase()
    .replaceAll("_", "-")}`;
  const statusLabel = STATUS_LABELS[normalizedStatus] || ticket.status || "Unknown";
  const statusColor = STATUS_COLORS[normalizedStatus] || "#686868";

  const isInvalidAssignee = (currentAssignee, newAssignee) =>
    currentAssignee !== "UNASSIGNED" &&
    currentAssignee !== null &&
    newAssignee === "UNASSIGNED";

  const isInvalidStatusTransition = (currentStatus, newStatus) => {
    if (currentStatus === newStatus) return true;
    if (currentStatus === "CLOSED" && newStatus !== "CLOSED") {
      return !hasRole("ROLE_ADMIN");
    }

    switch (currentStatus) {
      case "UNASSIGNED":
        return newStatus !== "CLOSED" && newStatus !== "IN_PROGRESS";
      case "IN_PROGRESS":
      case "BLOCKED":
      case "RESOLVED":
        return newStatus === "UNASSIGNED";
      case "CLOSED":
        return newStatus !== "CLOSED";
      default:
        return false;
    }
  };

  const handleStatus = async (event) => {
    const newStatus = event.target.value;
    const currentStatus = ticket.status;
    if (newStatus === currentStatus) return;

    if (isInvalidStatusTransition(currentStatus, newStatus)) {
      toast.error(`Invalid status transition: ${currentStatus} -> ${newStatus}`);
      return;
    }

    const updates =
      newStatus === "UNASSIGNED"
        ? { status: newStatus, assigneeName: "" }
        : { status: newStatus };

    try {
      await dispatch(updateTicket({ ticketId: ticket.id, updates })).unwrap();
      toast.success("Status updated successfully!");
    } catch (error) {
      toast.error(error || "An error occurred while updating status");
      dispatch(fetchTicketById(ticket.id));
    }
  };

  const handleAssignToMe = async () => {
    if (ticket.status === "CLOSED") {
      toast.error("Status changes are final. You cannot assign a closed ticket.");
      return;
    }
    try {
      await dispatch(assignTicketToMe(ticket.id)).unwrap();
      toast.success("Ticket assigned successfully.");
    } catch (error) {
      toast.error(error || "Assignment failed");
      dispatch(fetchTicketById(ticket.id));
    }
  };

  const handleAssignToSupport = async (event) => {
    if (ticket.status === "CLOSED") {
      toast.error("Status changes are final, you must reopen the ticket first.");
      return;
    }
    try {
      const value = event.target.value;
      const assignId = value === "UNASSIGNED" ? value : Number(value);
      setSelectedId(assignId);
      if (assignId !== "UNASSIGNED") {
        await dispatch(
          assignTicketToSupport({ ticketId: ticket.id, supportUserId: assignId })
        ).unwrap();
        toast.success("Ticket assigned successfully!");
      }
    } catch (error) {
      toast.error(error || "Assignment failed");
      dispatch(fetchTicketById(ticket.id));
    }
  };

  const formatTimestamp = (rawDate) => {
    if (!rawDate || rawDate === "N/A") return "N/A";
    const iso = rawDate.includes("T") ? rawDate : rawDate.replace(" ", "T");
    const utc = iso.endsWith("Z") ? iso : `${iso}Z`;
    const date = new Date(utc);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-US", {
      timeZone: "America/Chicago",
      year: "2-digit",
      month: "numeric",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
  };

  const displayCreationDate = formatTimestamp(ticket.createdAt);
  const displayAssignedDate = formatTimestamp(ticket.assignedAt);
  const displayAssignee =
    ticket.assigneeName ?? ticket.assignee ?? ticket.assignedTo ?? "N/A";

  const handleCloseTicket = () => {
    dispatch(setSelectedTicket(null));
    navigate("/Tickets", { replace: true });
    onClose?.();
  };

  return (
    <div className="ticket-modal-overlay">
    <Draggable
      handle=".ticket-drag-handle"
      cancel="button,input,textarea,select,option,a,[role='button'],.MuiButtonBase-root,.MuiSelect-select"
    >
      <div className="ticketdetailedview-container">
      <div data-testid="ticket-detailed-view" className="ticket-card">
        <button
          className="exit-btn"
          onClick={handleCloseTicket}
          data-testid="exit-button"
          aria-label="Close ticket details"
        >
          X
        </button>

        <div className="ticket-content" data-testid="ticket-content">
          <div className="ticket-header ticket-drag-handle" data-testid="ticket-header">
            <h2 className="ticket-heading" data-testid="ticket-heading">
              Ticket Title:<br />
              {ticket.title}<br />
              Ticket Number: {ticket.id}
            </h2>

            {(hasRole("ROLE_SUPPORT") || hasRole("ROLE_ADMIN") || hasRole("ROLE_TECHNOLOGY")) && (
              <ThemeProvider theme={theme}>
                <Button
                  className="assign-to-me-btn"
                  variant="text"
                  onClick={handleAssignToMe}
                  disabled={ticket.status === "CLOSED" || ticket.assigneeId === user.id}
                  data-testid="assign-to-me-button"
                >
                  {ticket.status === "CLOSED" ? "Ticket Closed" : "Assign to me"}
                </Button>
              </ThemeProvider>
            )}
          </div>

          <div className="ticket-detail-and-status" data-testid="ticket-detail-and-status">
            <div className="ticket-details" data-testid="ticket-details">
              <p>Ticket Creation Date: {displayCreationDate}</p>
              <p>Ticket Assignee: {displayAssignee}</p>
              <p>Assigned Date: {displayAssignedDate}</p>
            </div>

            <div className="dropdowns" data-testid="dropdowns">
              <div className="dropdown-status-wrapper">
                {hasRole("ROLE_SUPPORT") || hasRole("ROLE_ADMIN") || hasRole("ROLE_TECHNOLOGY") ? (
                  <FormControl>
                    <span className="dropdown-label">Ticket Status</span>
                    <Select
                      data-testid="dropdown-button1"
                      className={`dropdown-status ${statusClass}`}
                      value={ticket.status || ""}
                      onChange={handleStatus}
                      disabled={ticket.status === "CLOSED"}
                      sx={{ "& .MuiSelect-select": { color: statusColor } }}
                    >
                      <MenuItem value="UNASSIGNED" sx={{ color: STATUS_COLORS.UNASSIGNED }} disabled={isInvalidStatusTransition(ticket.status, "UNASSIGNED")}>Unassigned</MenuItem>
                      <MenuItem value="IN_PROGRESS" sx={{ color: STATUS_COLORS.IN_PROGRESS }} disabled={isInvalidStatusTransition(ticket.status, "IN_PROGRESS")}>In Progress</MenuItem>
                      <MenuItem value="BLOCKED" sx={{ color: STATUS_COLORS.BLOCKED }} disabled={isInvalidStatusTransition(ticket.status, "BLOCKED")}>Blocked</MenuItem>
                      <MenuItem value="RESOLVED" sx={{ color: STATUS_COLORS.RESOLVED }} disabled={isInvalidStatusTransition(ticket.status, "RESOLVED")}>Resolved</MenuItem>
                      {ticket.status === "CLOSED" && <MenuItem value="CLOSED" sx={{ color: STATUS_COLORS.CLOSED }}>Closed</MenuItem>}
                    </Select>
                  </FormControl>
                ) : (
                  <p className="status-text" data-testid="current-status">
                    Current Status:{" "}
                    <span className={`status-value ${statusClass}`} style={{ color: statusColor }}>
                      {statusLabel}
                    </span>
                  </p>
                )}
              </div>

              {hasRole("ROLE_ADMIN") && (
                <div className="dropdown-assign-wrapper">
                  <FormControl>
                    <span className="dropdown-label">Support User</span>
                    <Select
                      data-testid="dropdown-button2"
                      className="dropdown-assign"
                      value={selectedId}
                      onChange={handleAssignToSupport}
                      disabled={ticket.status === "CLOSED"}
                    >
                      <MenuItem value="UNASSIGNED" disabled={isInvalidAssignee(ticket.assigneeId, "UNASSIGNED")}>Select Assignee</MenuItem>
                      {supportUsers?.map((supportUser) => (
                        <MenuItem key={supportUser.id} value={supportUser.id}>
                          {supportUser.firstName} {supportUser.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </div>
          </div>

          <Box className="ticket-description-box">
            <Typography className="description-field">Description:</Typography>
            <Typography className="description-input-root" data-testid="ticket-description">
              {ticket.description || ""}
            </Typography>
          </Box>

          {showEdit && (
            <div className="edit-overlay">
              <div className="edit-modal">
                <button className="modal-close-x" onClick={() => setShowEdit(false)}>×</button>
                <label>Ticket Assignee:</label>
                <TextField
                  value={editFields.assigneeName}
                  onChange={(event) => setEditFields({ ...editFields, assigneeName: event.target.value })}
                />
              </div>
            </div>
          )}
        </div>

        <div className={`ticket-buttons ${ticket.status === "CLOSED" ? "closed-ticket-buttons" : ""}`}>
          <div className="ticket-button"><OpenDiscussionButton /></div>
          {(hasRole("ROLE_ADMIN") || hasRole("ROLE_SUPPORT") || hasRole("ROLE_TECHNOLOGY")) && ticket.status !== "CLOSED" && (
            <div className="ticket-button">
              <Popup
                modal
                nested
                trigger={<Tooltip title="Sets the Ticket Status to Closed" placement="right" arrow><Button variant="contained" className="close-btn">Close Ticket</Button></Tooltip>}
              >
                {(close) => <TicketChickenTest onClose={close} mode="complete" ticketId={ticket.id} />}
              </Popup>
            </div>
          )}
        </div>
      </div>
      </div>
    </Draggable>
    </div>
  );
}
